import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Area, ComposedChart } from "recharts";
import { formatCurrency, formatShortDate, formatDate } from "@/lib/format";
import { TrendingUp, TrendingDown } from "lucide-react";

/**
 * Performance panel — the visual centerpiece of the dashboard.
 * Large hero number, delta vs previous period, secondary metrics inline,
 * minimalist line chart. No nested cards.
 */
export default function PerformancePanel({ data, prevData, metric, setMetric, range, setRange, currency }) {
    const { t } = useTranslation();

    // Aggregate current window
    const totals = useMemo(() => (data || []).reduce((s, x) => ({
        revenue: s.revenue + (x.revenue || 0),
        occupancy: s.occupancy + (x.occupancy || 0),
        adr: s.adr + (x.adr || 0),
        revpar: s.revpar + (x.revpar || 0),
        count: s.count + 1,
    }), { revenue: 0, occupancy: 0, adr: 0, revpar: 0, count: 0 }), [data]);
    const avg = Math.max(1, totals.count);

    // Real period-over-period delta: sum current vs previous window
    const delta = useMemo(() => {
        if (!prevData || prevData.length === 0 || !data || data.length === 0) return null;
        const sumMetric = (arr) => arr.reduce((a, x) => a + (x[metric] || 0), 0);
        const c = sumMetric(data);
        const p = sumMetric(prevData);
        if (!p) return c > 0 ? 100 : null;
        return ((c - p) / p) * 100;
    }, [data, prevData, metric]);

    const ranges = [
        { n: 7,  key: "range_7d" },
        { n: 30, key: "range_30d" },
        { n: 90, key: "range_90d" },
    ];

    const metricDefs = [
        { key: "revenue",   label: t("dashboard.revenue"),      fmt: (v) => formatCurrency(v, currency),        hero: formatCurrency(totals.revenue, currency),                color: "#ff7b02" },
        { key: "occupancy", label: t("dashboard.occupancy"),    fmt: (v) => `${Math.round(v)}%`,                hero: `${Math.round(totals.occupancy / avg)}%`,                color: "#10b981" },
        { key: "adr",       label: t("dashboard.adr_short"),    fmt: (v) => formatCurrency(v, currency),        hero: formatCurrency(totals.adr / avg, currency),              color: "#3b82f6" },
        { key: "revpar",    label: t("dashboard.revpar_short"), fmt: (v) => formatCurrency(v, currency),        hero: formatCurrency(totals.revpar / avg, currency),           color: "#8b5cf6" },
    ];
    const activeDef = metricDefs.find((m) => m.key === metric) || metricDefs[0];

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="performance-card">
            {/* Header + range selector */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.hotel_performance")}
                </h2>
                <div className="flex items-center gap-1 rounded-md bg-muted p-0.5">
                    {ranges.map((r) => (
                        <button
                            key={r.n}
                            onClick={() => setRange(r.n)}
                            data-testid={`perf-range-${r.n}`}
                            className={`rounded px-2.5 py-1 text-[11.5px] font-medium transition ${
                                range === r.n
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {t(`dashboard.${r.key}`)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Hero metric */}
            <div className="px-5 pt-4">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {activeDef.label}
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                    <div className="text-[32px] font-semibold leading-none tracking-tight text-foreground tabular-nums">
                        {activeDef.hero}
                    </div>
                    {delta !== null && (
                        <div
                            className={`inline-flex items-center gap-1 text-[12.5px] font-medium ${
                                delta >= 0 ? "text-emerald-600" : "text-rose-600"
                            }`}
                        >
                            {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                            {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
                            <span className="text-muted-foreground/70 font-normal">
                                {t("dashboard.vs_prev_period")}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Secondary metric row — buttons that swap the chart */}
            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2 border-y border-border bg-muted/40 px-5 py-3">
                {metricDefs.map((m) => {
                    const active = m.key === metric;
                    return (
                        <button
                            key={m.key}
                            onClick={() => setMetric(m.key)}
                            data-testid={`perf-metric-${m.key}`}
                            className="group text-left"
                        >
                            <div className={`text-[10.5px] font-medium uppercase tracking-wider ${
                                active ? "text-[#ff7b02]" : "text-muted-foreground group-hover:text-foreground/90"
                            }`}>
                                {m.label}
                            </div>
                            <div className={`text-[16px] font-semibold tabular-nums ${
                                active ? "text-foreground" : "text-foreground/90 group-hover:text-foreground"
                            }`}>
                                {m.hero}
                            </div>
                            {active && (
                                <div className="mt-1 h-[2px] w-6 rounded bg-[#ff7b02]" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Chart */}
            <div className="h-[240px] px-3 py-4" data-testid="perf-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data}>
                        <defs>
                            <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={activeDef.color} stopOpacity={0.18} />
                                <stop offset="100%" stopColor={activeDef.color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(d) => formatShortDate(d)}
                            stroke="#94a3b8"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis
                            stroke="#94a3b8"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => metric === "occupancy" ? `${v}%` : v}
                            width={44}
                        />
                        <Tooltip
                            labelFormatter={(d) => formatDate(d)}
                            formatter={(v) => [activeDef.fmt(v), activeDef.label]}
                            contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, boxShadow: "0 4px 12px rgba(15,23,42,0.06)" }}
                        />
                        <Area
                            type="monotone"
                            dataKey={metric}
                            stroke="none"
                            fill="url(#perfGrad)"
                        />
                        <Line
                            type="monotone"
                            dataKey={metric}
                            stroke={activeDef.color}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </section>
    );
}
